import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getAllOutstandingClients, getSubClientsByClientOSReport,
    getClientOutStandingReport, getClientOutStandingReportInnerData,
} from '../../api/ReportsAPI/clientOutstandingReportAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

export const fetchOutstandingClients = createAsyncThunk('clientOutstandingReport/fetchClients', async (_, { rejectWithValue }) => {
    try { const res = await getAllOutstandingClients(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch clients')); }
});

export const fetchOutstandingSubClients = createAsyncThunk('clientOutstandingReport/fetchSubClients', async (clientcode, { rejectWithValue }) => {
    try { const res = await getSubClientsByClientOSReport(clientcode); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub-clients')); }
});

export const fetchClientOutstandingReport = createAsyncThunk('clientOutstandingReport/fetchReport', async (params, { rejectWithValue }) => {
    try { const res = await getClientOutStandingReport(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch client outstanding report')); }
});

export const fetchClientOutstandingDetail = createAsyncThunk('clientOutstandingReport/fetchDetail', async (params, { rejectWithValue }) => {
    try { const res = await getClientOutStandingReportInnerData(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch invoice detail')); }
});

const initialState = {
    clientList: [], clientListLoading: false,
    subClientList: [], subClientListLoading: false,
    report: [], reportLoading: false, reportError: null,
    detail: [], detailLoading: false, detailError: null,
};

const clientOutstandingReportSlice = createSlice({
    name: 'clientOutstandingReport',
    initialState,
    reducers: {
        clearSubClients: (s) => { s.subClientList = []; },
        clearClientOutstandingReport: (s) => { s.report = []; s.reportError = null; },
        clearClientOutstandingDetail: (s) => { s.detail = []; s.detailError = null; },
        resetClientOutstandingReport: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOutstandingClients.pending, (s) => { s.clientListLoading = true; })
            .addCase(fetchOutstandingClients.fulfilled, (s, a) => { s.clientListLoading = false; s.clientList = a.payload; })
            .addCase(fetchOutstandingClients.rejected, (s) => { s.clientListLoading = false; });

        builder
            .addCase(fetchOutstandingSubClients.pending, (s) => { s.subClientListLoading = true; })
            .addCase(fetchOutstandingSubClients.fulfilled, (s, a) => { s.subClientListLoading = false; s.subClientList = a.payload; })
            .addCase(fetchOutstandingSubClients.rejected, (s) => { s.subClientListLoading = false; s.subClientList = []; });

        builder
            .addCase(fetchClientOutstandingReport.pending, (s) => { s.reportLoading = true; s.reportError = null; })
            .addCase(fetchClientOutstandingReport.fulfilled, (s, a) => { s.reportLoading = false; s.report = a.payload; })
            .addCase(fetchClientOutstandingReport.rejected, (s, a) => { s.reportLoading = false; s.reportError = a.payload; s.report = []; });

        builder
            .addCase(fetchClientOutstandingDetail.pending, (s) => { s.detailLoading = true; s.detailError = null; })
            .addCase(fetchClientOutstandingDetail.fulfilled, (s, a) => { s.detailLoading = false; s.detail = a.payload; })
            .addCase(fetchClientOutstandingDetail.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = []; });
    },
});

export const {
    clearSubClients, clearClientOutstandingReport, clearClientOutstandingDetail, resetClientOutstandingReport,
} = clientOutstandingReportSlice.actions;

export const selectCORClientList          = (s) => s.clientOutstandingReport.clientList;
export const selectCORClientListLoading   = (s) => s.clientOutstandingReport.clientListLoading;
export const selectCORSubClientList       = (s) => s.clientOutstandingReport.subClientList;
export const selectCORSubClientListLoading = (s) => s.clientOutstandingReport.subClientListLoading;
export const selectCORReport              = (s) => s.clientOutstandingReport.report;
export const selectCORReportLoading       = (s) => s.clientOutstandingReport.reportLoading;
export const selectCORReportError         = (s) => s.clientOutstandingReport.reportError;
export const selectCORDetail              = (s) => s.clientOutstandingReport.detail;
export const selectCORDetailLoading       = (s) => s.clientOutstandingReport.detailLoading;

export default clientOutstandingReportSlice.reducer;
